/**
 * A card in the left column of the chat that can be folded away.
 *
 * The header row is the toggle. Controls that do something else, like stepping
 * between turns, are passed as `actions` and sit beside the toggle rather than
 * inside it, because one button cannot contain another.
 */
import { type ReactNode, useId } from "react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";

interface CollapsibleCardProps {
  /**
   * The icon shown before the title. Sized by the card, so pass the component
   * rather than an element.
   */
  icon: LucideIcon;
  title: string;
  /**
   * Sits next to the title, for the UnlikelyAI pill.
   */
  badge?: ReactNode;
  /**
   * Sits at the right of the header, for counts. Stays readable when the card is
   * folded, so the header alone says whether opening it is worth it.
   */
  meta?: ReactNode;
  /**
   * Buttons placed after the header, outside the toggle.
   */
  actions?: ReactNode;
  /**
   * Magenta means the card shows what UnlikelyAI produced.
   */
  tone?: "default" | "unlikely";
  /**
   * When open, the card takes a share of the leftover height instead of sizing
   * to its content.
   */
  grow?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  bodyClassName?: string;
  children: ReactNode;
}

export const CollapsibleCard = ({
  icon: Icon,
  title,
  badge,
  meta,
  actions,
  tone = "default",
  grow = false,
  isExpanded,
  onToggle,
  bodyClassName,
  children,
}: CollapsibleCardProps) => {
  const bodyId = useId();
  const isUnlikely = tone === "unlikely";

  return (
    <section
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border bg-brand-card",
        isUnlikely ? "border-unlikely/30" : "border-brand-border",
        isExpanded && grow ? "min-h-0 flex-1" : "flex-none"
      )}
    >
      <header
        className={cn(
          "flex flex-none items-center gap-1 px-3",
          isExpanded && "border-b border-brand-border",
          isExpanded && isUnlikely && "bg-unlikely/[0.04]"
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 rounded-md py-3 pl-1 text-left transition-colors hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-unlikely/40"
        >
          <ChevronRight
            className={cn(
              "h-[15px] w-[15px] flex-none transition-transform duration-200 motion-reduce:transition-none",
              isExpanded && "rotate-90",
              isUnlikely ? "text-unlikely" : "text-brand-muted"
            )}
            aria-hidden={true}
          />
          <Icon
            className={cn(
              "h-4 w-4 flex-none",
              isUnlikely ? "text-unlikely" : "text-brand-muted"
            )}
            aria-hidden={true}
          />
          <h2
            className={cn(
              "flex-none text-xs font-semibold uppercase tracking-[0.08em]",
              isUnlikely ? "text-unlikely" : "text-brand-muted"
            )}
          >
            {title}
          </h2>
          {badge}
          <span className="flex-1" />
          {meta && (
            <span className="flex flex-none items-center gap-1.5">{meta}</span>
          )}
        </button>
        {actions && (
          <div className="flex flex-none items-center gap-0.5 pl-1.5">
            {actions}
          </div>
        )}
      </header>

      {isExpanded && (
        <div
          id={bodyId}
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3.5",
            bodyClassName
          )}
        >
          {children}
        </div>
      )}
    </section>
  );
};
